import React, { FC, useState } from 'react';
import { Row, Col } from 'react-bootstrap';

import { useProtocolSequences } from '../useProtocolSequences';

import { PrimaryButton } from '../../../common/elements/Buttons/PrimaryButton';
import { Colors } from '../../../assets';
import { FontSize, FontWeight, Spacing } from '../../../assets/GlobalStyle/Variables';
import { ContentCard, ModalActions, ModalContent, ModalOverlay, ModalTitle } from '../styles';
import styled from 'styled-components';
import { SecondaryButton } from '../../../common';

const ActivateSequenceConfirm: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => (
  <ModalOverlay onClick={onCancel}>
    <ModalContent onClick={(e) => e.stopPropagation()}>
      <ModalTitle>Zahájení nové číselné řady</ModalTitle>
      <p>Opravdu chcete zahájit novou řadu? Tuto akci nelze vrátit zpět.</p>
      <ModalActions>
        <SecondaryButton type="button" onClick={onCancel}>
          Zrušit
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onConfirm}>
          Potrvdit
        </PrimaryButton>
      </ModalActions>
    </ModalContent>
  </ModalOverlay>
);

export const ProtocolSequenceManager: FC = (): JSX.Element => {

  const { 
    protocolSequences, 
    activeProtocolSequence, 
    nextProtocolSequence, 
    activateProtocolSequence, 
    loading,
    requestConfirmActivateProtocolSequence,
    confirmActivateSequence
  } = useProtocolSequences();

  const [ startNumber, setStartNumber ] = useState("1");
  const [ selectedProtocolSequence, setSelectedProtocolSequence ] = useState<number | string>('');

  const handleActivateProtocolSequence = async (year: number, startNumber: number) => {
    await activateProtocolSequence(year, startNumber);
    setStartNumber("1");
    setSelectedProtocolSequence('');
    requestConfirmActivateProtocolSequence(null);
  }

  return (
    <>
      <ContentCard>
        {!loading && (
          <ProtocolSequencesForm>
            <Row>
              <Col md={3}>
                <StyledLabel>Současná řada:</StyledLabel>
              </Col>
              <Col md={9}>
              <StyledReadOnlyField>{activeProtocolSequence ? `Pitevní protokoly ${activeProtocolSequence.active_year}` : ''}</StyledReadOnlyField>
            </Col>
          </Row>
          
          <Row>
            <Col md={3}>
              <StyledLabel>Zahájit a přepnout na novou řadu:</StyledLabel>
            </Col>
            <Col md={3}>
              <PrimaryButton onClick={() => requestConfirmActivateProtocolSequence(nextProtocolSequence?.active_year ?? 1)} disabled={loading}>
                {nextProtocolSequence ? `Pitevní protokoly ${nextProtocolSequence.active_year}` : ''}
              </PrimaryButton>
            </Col>
            <Col md={2}>
              <StyledLabel>Počáteční číslo protokolu:</StyledLabel>
            </Col>
            <Col md={3}>
              <StyledField
                type="text"
                value={startNumber}
                pattern="[0-9]*"
                inputMode="numeric"
                onChange={e => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    setStartNumber(value);
                  }
                }}
              />
            </Col>
          </Row>
        </ProtocolSequencesForm>)}     
      </ContentCard>

      {confirmActivateSequence !== null && (
        <ActivateSequenceConfirm onConfirm={() => handleActivateProtocolSequence(nextProtocolSequence?.active_year ?? 0, Number(startNumber))} onCancel={() => requestConfirmActivateProtocolSequence(null)} />
      )}
    </>
  );
};

const ProtocolSequencesForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px;
`;

const StyledField = styled.input<{ $hasError?: boolean }>`
  padding: 0 ${Spacing(8)};
  border: 1px solid ${(props) => (props.$hasError ? Colors.error : Colors.disabledHover)};
  line-height: 40px;
  height: 42px;
  box-sizing: border-box;
  width: 100%;
  border-radius: 0px;
  color: ${Colors.grey};
  font-size: ${FontSize.normal};

  &:focus {
    outline: none;
    border: 1px solid ${(props) => (props.$hasError ? Colors.error : Colors.disabledHover)};
  }
`;

const StyledReadOnlyField = styled.label`
  margin: 0;
  padding: 0 ${Spacing(4)};
  color: ${Colors.grey};
  font-weight: ${FontWeight.normal};
  font-size: ${FontSize.normal};
  white-space: nowrap;
  height: 40px;
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  margin: 0;
  color: ${Colors.grey};
  font-weight: ${FontWeight.strong};
  font-size: ${FontSize.normal};
  white-space: nowrap;
  height: 40px;
  display: flex;
  align-items: center;
`;

export const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${Colors.disabledHover};
  border-radius: 0;
  color: ${Colors.grey};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Colors.secondary};
  }

  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 3px rgba(60, 17, 73, 0.1);
  }
`;